import { useState, useCallback, useRef, useEffect } from 'react';

export default function useUndoRedo(initialState, maxHistoryLength = 50) {
  const [state, setState] = useState(initialState);
  const [history, setHistory] = useState([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isUndoRedoAction = useRef(false);
  const debounceTimer = useRef(null);
  const pendingState = useRef(null);

  const updateState = useCallback((newState) => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }

    setState(newState);
    pendingState.current = newState;
    
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      const stateToSave = pendingState.current;
      if (stateToSave) {
        setHistory(prev => {
          const newHistory = prev.slice(0, currentIndex + 1);
          
          if (newHistory.length > 0 && JSON.stringify(newHistory[newHistory.length - 1]) === JSON.stringify(stateToSave)) {
            return prev;
          }
          
          newHistory.push(stateToSave);
          
          if (newHistory.length > maxHistoryLength) {
            newHistory.shift();
            return newHistory;
          }
          
          return newHistory;
        });
        
        setCurrentIndex(prev => {
          const newIndex = prev + 1;
          return newIndex >= maxHistoryLength ? maxHistoryLength - 1 : newIndex;
        });
        
        pendingState.current = null;
      }
    }, 500);
  }, [currentIndex, maxHistoryLength]);
  
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      isUndoRedoAction.current = true;
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setState(history[newIndex]);
    }
  }, [currentIndex, history]);

  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setState(history[newIndex]);
    }
  }, [currentIndex, history]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return {
    state,
    updateState,
    undo,
    redo,
    canUndo,
    canRedo
  };
}
